/**
 * O rótulo ÚNICO do turno em andamento (11/09/2026) — o texto que a linha de
 * raciocínio mostra, com o shimmer no próprio texto.
 *
 * Antes eram quatro indicadores empilhados para o mesmo instante: a pílula de
 * fase na topbar, o "Raciocínio da IA" com shimmer, o "Montando a resposta..."
 * do `CartaoPendente` e o rótulo do rodapé. Sobrou um, e a copy dele muda com
 * o que o turno está fazendo:
 *
 * | o turno está... | rótulo | fonte |
 * |---|---|---|
 * | recém-enviado, nada chegou | `Pensando...` | `persona.textoDePreparo` (`textoInicial`) |
 * | raciocinando (`thinking`) | `Raciocinando` | `FASE_LABELS.reasoning` |
 * | com uma tool rodando | `Consultando cliente...` | o `label` da PARTE, ou `TOOL_LABELS[tool]` |
 * | num nó do Supervisor, sem tool em voo (`node-status`) | `Entendendo o pedido...` | `ROTULO_DO_NO_DO_TURNO` (ver `rotuloDoNoDoTurno`) |
 * | lendo o resultado da tool (fase `tools`, nenhuma em voo) | `Raciocinando` | `FASE_LABELS.reasoning` |
 * | escrevendo (`token`) | `Elaborando resposta` | `FASE_LABELS.answerDireto` |
 * | no painel do comitê | `Consultando agentes` / `Consolidando parecer` | a máquina de fase |
 *
 * ## As DUAS fontes do rótulo da tool, e por que as duas ficam
 *
 * O fio traz a mesma chamada por dois canais (`core/sse.py`, dual-emit):
 *
 * - `tool_call` (antigo) → o store guarda o NOME em `toolCalls`, e o nome vira
 *   frase pelo `TOOL_LABELS` — um espelho MANUAL do `LABEL` de cada tool do
 *   backend, que envelhece (`Text.tool-labels.unit.js` é quem o cobra);
 * - `tool-input-available` (tipado) → o store guarda a PARTE em `toolParts`,
 *   e ela pode trazer o `label` pronto, montado no backend a partir de
 *   `BaseTool.LABEL`.
 *
 * O `label` da parte VENCE: é a frase que o próprio dono da tool declarou, e
 * chega sem depender de o espelho estar em dia. Mas ele NÃO cobre tudo hoje —
 * medido no fio real em 17/09/2026 (escritório 83, backend local), o caminho
 * legado (`usecase_ia_crm_chat.py`, `tool_use_complete`) só escreve `label`
 * para tool de FRONT:
 *
 *     event: tool-input-available
 *     data: {"toolCallId":"toolu_01JFkt…","tool":"list_clientes_carteira",
 *            "input":{…},"executor":"server"}        ← sem `label`
 *
 * O caminho do Supervisor (`core/supervisor/fabrica.py::_emitir_chamada_da_
 * tool`) escreve para todas. Por isso a cascata parte → `TOOL_LABELS`: tirar
 * o segundo degrau apagaria o rótulo de TODA tool de servidor no caminho
 * legado, e a tela voltaria a dizer "Pensando..." com uma consulta rodando.
 *
 * ⚠️ O nome técnico da tool NUNCA vira pixel (AC-2.6): sem rótulo em nenhum
 * dos dois degraus, a tool é pulada e quem fala é a fase.
 *
 * Função PURA: recebe a fase (`faseDoTurno` do store), as partes do turno
 * (`toolPartsOrdenadas`), a lista de tools em voo (`toolCalls`) e o texto
 * inicial da persona. Sem `?.`/`??` (o Babel deste projeto não transpila).
 */
import { TOOL_LABELS } from '@modules/aawz-intelligence-v2/constants/Text'
import { ROTULO_DO_NO_DO_TURNO } from '@modules/aawz-intelligence-v2/constants/TextChat'
import {
  ESTADO_DO_NO,
  ORDEM_DOS_NOS_DO_SUPERVISOR,
} from '@modules/aawz-intelligence-v2/constants/NosDoSupervisor'
import { FASE_LABELS, FASE_TOOLS } from '@modules/aawz-intelligence-v2/utils/fase-do-turno'
import { TOOL_PART_STATE } from '@modules/aawz-intelligence-v2/store/toolParts'

// O que a linha diz quando nem a persona trouxe um texto inicial.
export const ROTULO_INICIAL_DO_TURNO = 'Pensando...'

/**
 * O rótulo é o GENÉRICO de preparo ("Pensando...") em vez de uma etapa de
 * verdade?
 *
 * Quem pergunta é o tempo mínimo na tela (`chat-message.passo.js`): uma etapa
 * real segura a tela para ser lida; o genérico não tem o que ler e cede o
 * lugar assim que a primeira etapa é conhecida — é justamente o "não ficar com
 * Pensando durante um tempo" que motivou a mudança.
 */
export function ehRotuloDePreparo(rotulo, textoInicial) {
  if (!rotulo) return true
  return rotulo === (textoInicial || ROTULO_INICIAL_DO_TURNO)
}

/**
 * A frase de tela de UMA parte de tool: o `label` que o backend mandou, ou o
 * espelho `TOOL_LABELS` do nome. Nunca o nome cru.
 */
export function rotuloDaParte(parte) {
  if (!parte) return ''
  const doBackend = typeof parte.label === 'string' ? parte.label.trim() : ''
  if (doBackend) return doBackend
  // ⚠️ **NÃO normalize o prefixo `mcp__` aqui** — e isto é o oposto do que a
  // [T082] fez no cartão, de propósito.
  //
  // No caminho do agente a MESMA chamada entra no slice sob DOIS nomes: o
  // técnico (`mcp__aawz__list_tarefas`) e o de sempre (`list_tarefas`). Só um
  // dos dois pode falar. Devolver `''` para o técnico é o que o faz ser PULADO,
  // e quem fala é o gêmeo de nome humano — medido na tela em 17/09/2026: sem
  // essa régua o consultor lia `"mcp__aawz__list_oportunidades..."` no meio da
  // conversa, porque o técnico é a última parte em voo em metade dos casos.
  //
  // ★ No CARTÃO o oposto é verdade: lá só UMA parte carrega a proposta, e não
  // casar o nome prefixado significa nenhum cartão (G7). Mesmo mecanismo,
  // decisões opostas — porque no rótulo há dois candidatos e no cartão há um.
  return TOOL_LABELS[parte.tool] || ''
}

/**
 * A tool de servidor mais recente ainda em voo — a ÚLTIMA da lista, porque o
 * store empilha na ordem de chegada e a mais nova é a que o modelo espera.
 * Só conta a que tem rótulo humano: uma tool sem `TOOL_LABELS` (nome interno)
 * nunca vira pixel na conversa.
 */
export function rotuloDaToolEmVoo(toolsEmVoo) {
  const lista = Array.isArray(toolsEmVoo) ? toolsEmVoo : []
  for (let i = lista.length - 1; i >= 0; i -= 1) {
    const rotulo = TOOL_LABELS[lista[i]]
    if (rotulo) return rotulo
  }
  return ''
}

/**
 * A etapa corrente a partir das PARTES do turno (`toolPartsOrdenadas`): a
 * ÚLTIMA parte que ainda está em `input-available` — a mesma régua do
 * `rotuloDaToolEmVoo` (a mais nova fala), agora sobre o canal tipado.
 *
 * Parte já resolvida (`output-*`) não conta: o dado dela chegou, e dizer
 * "Consultando X..." sobre um resultado que já está na mão é afirmar algo
 * falso.
 */
export function rotuloDaParteEmExecucao(partesDoTurno) {
  const lista = Array.isArray(partesDoTurno) ? partesDoTurno : []
  for (let i = lista.length - 1; i >= 0; i -= 1) {
    const parte = lista[i]
    if (!parte || parte.state !== TOOL_PART_STATE.ENTRADA) continue
    const rotulo = rotuloDaParte(parte)
    if (rotulo) return rotulo
  }
  return ''
}

/**
 * O rótulo do NÓ do Supervisor em que o turno está (`node-status`, canal
 * `noDoTurno` do store) — ★ [review #2364, item D].
 *
 * `iniciado` fala o rótulo do PRÓPRIO nó. `concluido` fala o do PRÓXIMO nó
 * com rótulo humano na `ORDEM_DOS_NOS_DO_SUPERVISOR`: VERIFICADO no checkout
 * irmão, o único produtor (`grafo.py::executar_turno`) emite só `concluido`,
 * ao FIM de cada nó — um `concluido` diz onde o turno está AGORA, que é o nó
 * seguinte. Sem esta leitura a linha ficaria um passo atrás o turno inteiro.
 * `falhou` não pinta nada (o erro do turno tem canal próprio), e nó sem
 * rótulo (infra, condicional no-op, desconhecido) devolve `''` — a cascata
 * segue para a fase; o slug cru nunca vira pixel.
 *
 * @param {?{no: string, estado: string}} noDoTurno O getter `noDoTurno`.
 * @returns {string} `''` quando não há o que dizer.
 * @example rotuloDoNoDoTurno({ no: 'understand_request', estado: 'concluido' }) // → 'Reunindo o contexto...'
 */
export function rotuloDoNoDoTurno(noDoTurno) {
  if (!noDoTurno || typeof noDoTurno.no !== 'string' || !noDoTurno.no) return ''
  if (noDoTurno.estado === ESTADO_DO_NO.INICIADO) return ROTULO_DO_NO_DO_TURNO[noDoTurno.no] || ''
  if (noDoTurno.estado !== ESTADO_DO_NO.CONCLUIDO) return ''
  const posicao = ORDEM_DOS_NOS_DO_SUPERVISOR.indexOf(noDoTurno.no)
  if (posicao < 0) return ''
  for (let i = posicao + 1; i < ORDEM_DOS_NOS_DO_SUPERVISOR.length; i += 1) {
    const rotulo = ROTULO_DO_NO_DO_TURNO[ORDEM_DOS_NOS_DO_SUPERVISOR[i]]
    if (rotulo) return rotulo
  }
  return ''
}

/**
 * @param {Object=}   opcoes
 * @param {Object=}   opcoes.fase           `{phase, label}` de `utils/fase-do-turno.js`, ou `null`.
 * @param {Object[]=} opcoes.partesDoTurno  As partes do turno (getter `toolPartsOrdenadas`).
 * @param {string[]=} opcoes.toolsEmVoo     Nomes das tools em execução (getter `toolCalls`).
 * @param {Object=}   opcoes.noDoTurno      `{no, estado}` do último `node-status` (getter `noDoTurno`).
 * @param {string=}   opcoes.textoInicial   O `textoDePreparo` da persona.
 * @returns {string} Nunca vazio.
 */
export function rotuloDoTurno({ fase, partesDoTurno, toolsEmVoo, noDoTurno, textoInicial } = {}) {
  const daParte = rotuloDaParteEmExecucao(partesDoTurno)
  if (daParte) return daParte
  const daTool = rotuloDaToolEmVoo(toolsEmVoo)
  if (daTool) return daTool
  // O nó do Supervisor fica ABAIXO da tool (a tool é mais específica) e
  // ACIMA da fase (a fase é derivada dos eventos; o nó é declarado).
  const doNo = rotuloDoNoDoTurno(noDoTurno)
  if (doNo) return doNo
  if (fase && fase.phase === FASE_TOOLS) return FASE_LABELS.reasoning
  if (fase && fase.label) return fase.label
  return textoInicial || ROTULO_INICIAL_DO_TURNO
}
